import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useAuthApi } from "../hooks/useAuthApi";
import { PLATFORM } from "../env";
import type {
  FinanceAuditEvent,
  FinanceDunningCase,
  FinanceDocument,
  FinancePayout,
  FinanceReconciliationBatch,
  FinanceSummary,
  FinanceTaxProfile,
  StripeInfo,
  Tenant,
  TenantDriverMembership,
  TenantUser,
} from "../types";

interface Props {
  id: number;
  onBack: () => void;
  onOpenDriver: (id: number) => void;
}

interface MembershipPage { items: TenantDriverMembership[]; }

const integrationModeLabel = (mode: string): string => {
  if (mode === "shared") return "Shared";
  if (mode === "invite_only") return "Invite Only";
  if (mode === "isolated") return "Isolated";
  return mode;
};

const integrationModeColor = (mode: string): "info" | "success" | "default" => {
  if (mode === "shared") return "info";
  if (mode === "invite_only") return "success";
  return "default";
};

const stripeStatusColor = (s: string | null | undefined): "success" | "warning" | "error" | "default" => {
  if (s === "active") return "success";
  if (s === "past_due" || s === "unpaid") return "warning";
  if (s === "canceled") return "error";
  return "default";
};

export default function TenantDetail({ id, onBack, onOpenDriver }: Props) {
  const authApi = useAuthApi();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [memberships, setMemberships] = useState<TenantDriverMembership[]>([]);
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [stripeInfo, setStripeInfo] = useState<StripeInfo | null>(null);
  const [financeSummary, setFinanceSummary] = useState<FinanceSummary | null>(null);
  const [financeTaxProfile, setFinanceTaxProfile] = useState<FinanceTaxProfile | null>(null);
  const [financeDocuments, setFinanceDocuments] = useState<FinanceDocument[]>([]);
  const [financeReconciliationBatches, setFinanceReconciliationBatches] = useState<FinanceReconciliationBatch[]>([]);
  const [financePayouts, setFinancePayouts] = useState<FinancePayout[]>([]);
  const [financeDunningCases, setFinanceDunningCases] = useState<FinanceDunningCase[]>([]);
  const [financeAuditEvents, setFinanceAuditEvents] = useState<FinanceAuditEvent[]>([]);
  const [financeEditOpen, setFinanceEditOpen] = useState(false);
  const [financeCountryCode, setFinanceCountryCode] = useState("");
  const [financeVatId, setFinanceVatId] = useState("");
  const [financeVatRate, setFinanceVatRate] = useState("0");
  const [financeReverseCharge, setFinanceReverseCharge] = useState(false);
  const [financeNotes, setFinanceNotes] = useState("");
  const [financeSaving, setFinanceSaving] = useState(false);
  const [financeActionLoadingId, setFinanceActionLoadingId] = useState<number | null>(null);
  const [financeActionError, setFinanceActionError] = useState<string | null>(null);
  const [financeBatchLoading, setFinanceBatchLoading] = useState(false);
  const [financePayoutProvider, setFinancePayoutProvider] = useState("manual");
  const [financePayoutExternalId, setFinancePayoutExternalId] = useState("");
  const [financePayoutGross, setFinancePayoutGross] = useState("");
  const [financePayoutFee, setFinancePayoutFee] = useState("0");
  const [financePayoutStatus, setFinancePayoutStatus] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tokenRevealed, setTokenRevealed] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingTenant, setDeletingTenant] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Integration mode edit
  const [modeEditOpen, setModeEditOpen] = useState(false);
  const [modeInput, setModeInput] = useState("");
  const [modeSaving, setModeSaving] = useState(false);
  const [modeEditError, setModeEditError] = useState<string | null>(null);

  // Stripe edit
  const [stripeEditOpen, setStripeEditOpen] = useState(false);
  const [stripeCustomerIdInput, setStripeCustomerIdInput] = useState("");
  const [stripeSubIdInput, setStripeSubIdInput] = useState("");
  const [stripeSaving, setStripeSaving] = useState(false);
  const [stripeEditError, setStripeEditError] = useState<string | null>(null);

  // OCPP gateway rate-limit override edit
  const [rateLimitEditOpen, setRateLimitEditOpen] = useState(false);
  const [rateLimitOverrideEnabledInput, setRateLimitOverrideEnabledInput] = useState("disabled");
  const [rateLimitMaxRequestsInput, setRateLimitMaxRequestsInput] = useState("");
  const [rateLimitSaving, setRateLimitSaving] = useState(false);
  const [rateLimitEditError, setRateLimitEditError] = useState<string | null>(null);

  // Add user dialog
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [addUserEmail, setAddUserEmail] = useState("");
  const [addUserName, setAddUserName] = useState("");
  const [addUserRole, setAddUserRole] = useState("admin");
  const [addingUser, setAddingUser] = useState(false);
  const [addUserError, setAddUserError] = useState<string | null>(null);

  const [userActionError, setUserActionError] = useState<string | null>(null);

  const loadUsers = useCallback(() => {
    authApi<TenantUser[]>(`${PLATFORM}/tenants/${id}/users`)
      .then((data) => setUsers(data ?? []))
      .catch(() => {/* non-critical */});
  }, [authApi, id]);

  const loadFinance = useCallback(() => {
    Promise.all([
      authApi<FinanceSummary>(`${PLATFORM}/tenants/${id}/finance/summary`),
      authApi<FinanceTaxProfile | null>(`${PLATFORM}/tenants/${id}/finance/tax-profile`),
      authApi<{ items: FinanceDocument[] }>(`${PLATFORM}/tenants/${id}/finance/documents?limit=10`),
      authApi<FinanceReconciliationBatch[]>(`${PLATFORM}/tenants/${id}/finance/reconciliation/batches`),
      authApi<FinancePayout[]>(`${PLATFORM}/tenants/${id}/finance/payouts`),
      authApi<FinanceDunningCase[]>(`${PLATFORM}/tenants/${id}/finance/dunning/cases`),
      authApi<FinanceAuditEvent[]>(`${PLATFORM}/tenants/${id}/finance/audit-events?limit=10`),
    ])
      .then(([summary, taxProfile, docs, batches, payouts, dunningCases, auditEvents]) => {
        setFinanceSummary(summary);
        setFinanceTaxProfile(taxProfile ?? null);
        setFinanceDocuments(docs?.items ?? []);
        setFinanceReconciliationBatches(batches ?? []);
        setFinancePayouts(payouts ?? []);
        setFinanceDunningCases(dunningCases ?? []);
        setFinanceAuditEvents(auditEvents ?? []);
      })
      .catch((e: unknown) => {
        setFinanceActionError(e instanceof Error ? e.message : "Failed to load finance data");
      });
  }, [authApi, id]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      authApi<Tenant>(`${PLATFORM}/tenants/${id}`),
      authApi<MembershipPage>(`${PLATFORM}/memberships?tenant_id=${id}&limit=50&offset=0`),
      authApi<TenantUser[]>(`${PLATFORM}/tenants/${id}/users`),
      authApi<StripeInfo>(`${PLATFORM}/tenants/${id}/stripe`),
      authApi<FinanceSummary>(`${PLATFORM}/tenants/${id}/finance/summary`),
      authApi<FinanceTaxProfile | null>(`${PLATFORM}/tenants/${id}/finance/tax-profile`),
      authApi<{ items: FinanceDocument[] }>(`${PLATFORM}/tenants/${id}/finance/documents?limit=5`),
      authApi<FinanceReconciliationBatch[]>(`${PLATFORM}/tenants/${id}/finance/reconciliation/batches`),
      authApi<FinancePayout[]>(`${PLATFORM}/tenants/${id}/finance/payouts`),
      authApi<FinanceDunningCase[]>(`${PLATFORM}/tenants/${id}/finance/dunning/cases`),
      authApi<FinanceAuditEvent[]>(`${PLATFORM}/tenants/${id}/finance/audit-events?limit=10`),
    ])
      .then(([t, m, u, s, summary, taxProfile, docs, batches, payouts, dunningCases, auditEvents]) => {
        if (cancelled) return;
        setTenant(t);
        setMemberships(m?.items ?? []);
        setUsers(u ?? []);
        setStripeInfo(s);
        setFinanceSummary(summary);
        setFinanceTaxProfile(taxProfile ?? null);
        setFinanceDocuments(docs?.items ?? []);
        setFinanceReconciliationBatches(batches ?? []);
        setFinancePayouts(payouts ?? []);
        setFinanceDunningCases(dunningCases ?? []);
        setFinanceAuditEvents(auditEvents ?? []);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load tenant");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [authApi, id]);

  const handleStatusToggle = () => {
    if (!tenant) return;
    const newStatus = tenant.status === "active" ? "suspended" : "active";
    setStatusUpdating(true);
    setStatusError(null);
    authApi<Tenant>(`${PLATFORM}/tenants/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ status: newStatus }),
    })
      .then((updated) => { if (updated) setTenant(updated); })
      .catch((e: unknown) => setStatusError(e instanceof Error ? e.message : "Failed to update status"))
      .finally(() => setStatusUpdating(false));
  };

  const openStripeEdit = () => {
    setStripeCustomerIdInput(tenant?.stripe_customer_id ?? "");
    setStripeSubIdInput(tenant?.stripe_subscription_id ?? "");
    setStripeEditError(null);
    setStripeEditOpen(true);
  };

  const openModeEdit = () => {
    setModeInput(tenant?.integration_mode ?? "invite_only");
    setModeEditError(null);
    setModeEditOpen(true);
  };

  const handleModeSave = () => {
    setModeSaving(true);
    setModeEditError(null);
    authApi<Tenant>(`${PLATFORM}/tenants/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ integration_mode: modeInput }),
    })
      .then((updated) => {
        if (updated) setTenant(updated);
        setModeEditOpen(false);
      })
      .catch((e: unknown) => setModeEditError(e instanceof Error ? e.message : "Failed to save"))
      .finally(() => setModeSaving(false));
  };

  const handleStripeSave = () => {
    setStripeSaving(true);
    setStripeEditError(null);
    const body: Record<string, string | null> = {
      stripe_customer_id: stripeCustomerIdInput.trim() || null,
      stripe_subscription_id: stripeSubIdInput.trim() || null,
    };
    authApi<Tenant>(`${PLATFORM}/tenants/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    })
      .then((updated) => {
        if (updated) setTenant(updated);
        setStripeEditOpen(false);
        // Reload Stripe live info
        authApi<StripeInfo>(`${PLATFORM}/tenants/${id}/stripe`)
          .then((s) => setStripeInfo(s))
          .catch(() => {/* non-critical */});
      })
      .catch((e: unknown) => setStripeEditError(e instanceof Error ? e.message : "Failed to save"))
      .finally(() => setStripeSaving(false));
  };

  const openRateLimitEdit = () => {
    setRateLimitOverrideEnabledInput(
      tenant?.ocpp_gateway_rate_limit_override_enabled ? "enabled" : "disabled"
    );
    setRateLimitMaxRequestsInput(
      tenant?.ocpp_gateway_rate_limit_override_max_requests != null
        ? String(tenant.ocpp_gateway_rate_limit_override_max_requests)
        : ""
    );
    setRateLimitEditError(null);
    setRateLimitEditOpen(true);
  };

  const handleRateLimitSave = () => {
    setRateLimitSaving(true);
    setRateLimitEditError(null);
    const overrideEnabled = rateLimitOverrideEnabledInput === "enabled";
    const trimmedMaxRequests = rateLimitMaxRequestsInput.trim();
    const body: Record<string, boolean | number | null> = {
      ocpp_gateway_rate_limit_override_enabled: overrideEnabled,
      ocpp_gateway_rate_limit_override_max_requests: trimmedMaxRequests
        ? Number(trimmedMaxRequests)
        : null,
    };
    authApi<Tenant>(`${PLATFORM}/tenants/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    })
      .then((updated) => {
        if (updated) setTenant(updated);
        setRateLimitEditOpen(false);
      })
      .catch((e: unknown) => setRateLimitEditError(e instanceof Error ? e.message : "Failed to save"))
      .finally(() => setRateLimitSaving(false));
  };

  const openFinanceEdit = () => {
    setFinanceCountryCode(financeTaxProfile?.default_country_code ?? "");
    setFinanceVatId(financeTaxProfile?.vat_id ?? "");
    setFinanceVatRate(String(financeTaxProfile?.default_vat_rate_percent ?? 0));
    setFinanceReverseCharge(financeTaxProfile?.reverse_charge_enabled ?? false);
    setFinanceNotes(financeTaxProfile?.notes ?? "");
    setFinanceActionError(null);
    setFinanceEditOpen(true);
  };

  const saveFinanceTaxProfile = () => {
    const vatRate = Number.parseFloat(financeVatRate);
    if (!Number.isFinite(vatRate) || vatRate < 0) {
      setFinanceActionError("VAT rate must be a valid non-negative number");
      return;
    }
    setFinanceSaving(true);
    setFinanceActionError(null);
    authApi<FinanceTaxProfile>(`${PLATFORM}/tenants/${id}/finance/tax-profile`, {
      method: "PUT",
      body: JSON.stringify({
        default_country_code: financeCountryCode.trim() || null,
        vat_id: financeVatId.trim() || null,
        default_vat_rate_percent: vatRate,
        reverse_charge_enabled: financeReverseCharge,
        is_active: true,
        notes: financeNotes.trim() || null,
      }),
    })
      .then((updated) => {
        setFinanceTaxProfile(updated);
        setFinanceEditOpen(false);
      })
      .catch((e: unknown) => setFinanceActionError(e instanceof Error ? e.message : "Failed to save tax profile"))
      .finally(() => setFinanceSaving(false));
  };

  const runFinanceReconciliation = () => {
    setFinanceBatchLoading(true);
    setFinanceActionError(null);
    authApi(`${PLATFORM}/tenants/${id}/finance/reconciliation/run`, {
      method: "POST",
      body: JSON.stringify({ source_system: "manual", statement_reference: `tenant-${id}` , currency: "EUR" }),
    })
      .then(() => loadFinance())
      .catch((e: unknown) => setFinanceActionError(e instanceof Error ? e.message : "Failed to run reconciliation"))
      .finally(() => setFinanceBatchLoading(false));
  };

  const runFinanceDunning = () => {
    setFinanceBatchLoading(true);
    setFinanceActionError(null);
    authApi(`${PLATFORM}/tenants/${id}/finance/dunning/run`, { method: "POST" })
      .then(() => loadFinance())
      .catch((e: unknown) => setFinanceActionError(e instanceof Error ? e.message : "Failed to run dunning"))
      .finally(() => setFinanceBatchLoading(false));
  };

  const createFinanceCreditNote = (documentId: number) => {
    setFinanceActionLoadingId(documentId);
    setFinanceActionError(null);
    authApi<FinanceDocument>(`${PLATFORM}/tenants/${id}/finance/documents/${documentId}/credit-note`, {
      method: "POST",
    })
      .then(() => loadFinance())
      .catch((e: unknown) => setFinanceActionError(e instanceof Error ? e.message : "Failed to create credit note"))
      .finally(() => setFinanceActionLoadingId(null));
  };

  const createFinanceRefund = (documentId: number) => {
    setFinanceActionLoadingId(documentId);
    setFinanceActionError(null);
    authApi<FinanceDocument>(`${PLATFORM}/tenants/${id}/finance/documents/${documentId}/refund`, {
      method: "POST",
    })
      .then(() => loadFinance())
      .catch((e: unknown) => setFinanceActionError(e instanceof Error ? e.message : "Failed to create refund"))
      .finally(() => setFinanceActionLoadingId(null));
  };

  const createFinancePayout = () => {
    const gross = Number.parseFloat(financePayoutGross);
    const fee = Number.parseFloat(financePayoutFee);
    if (!financePayoutExternalId.trim()) {
      setFinanceActionError("Payout reference is required");
      return;
    }
    if (!Number.isFinite(gross) || gross <= 0 || !Number.isFinite(fee) || fee < 0) {
      setFinanceActionError("Payout amount and fee must be valid numbers");
      return;
    }
    setFinanceBatchLoading(true);
    setFinanceActionError(null);
    authApi<FinancePayout>(`${PLATFORM}/tenants/${id}/finance/payouts`, {
      method: "POST",
      body: JSON.stringify({
        provider: financePayoutProvider.trim() || "manual",
        provider_payout_id: financePayoutExternalId.trim(),
        currency: "EUR",
        amount_gross: gross,
        fee_amount: fee,
        status: financePayoutStatus,
        payload: {},
      }),
    })
      .then(() => {
        setFinancePayoutExternalId("");
        setFinancePayoutGross("");
        setFinancePayoutFee("0");
        loadFinance();
      })
      .catch((e: unknown) => setFinanceActionError(e instanceof Error ? e.message : "Failed to save payout"))
      .finally(() => setFinanceBatchLoading(false));
  };

  const handleAddUser = () => {
    setAddingUser(true);
    setAddUserError(null);
    authApi<TenantUser>(`${PLATFORM}/tenants/${id}/users`, {
      method: "POST",
      body: JSON.stringify({ email: addUserEmail.trim(), role: addUserRole, name: addUserName.trim() || undefined }),
    })
      .then(() => {
        setAddUserOpen(false);
        setAddUserEmail("");
        setAddUserName("");
        setAddUserRole("admin");
        loadUsers();
      })
      .catch((e: unknown) => setAddUserError(e instanceof Error ? e.message : "Failed to add user"))
      .finally(() => setAddingUser(false));
  };

  const handleChangeRole = (userId: number, currentRole: string) => {
    const newRole = currentRole === "admin" ? "operator" : "admin";
    setUserActionError(null);
    authApi<TenantUser>(`${PLATFORM}/tenants/${id}/users/${userId}`, {
      method: "PATCH",
      body: JSON.stringify({ role: newRole }),
    })
      .then(() => loadUsers())
      .catch((e: unknown) => setUserActionError(e instanceof Error ? e.message : "Failed to update role"));
  };

  const handleRemoveUser = (userId: number) => {
    setUserActionError(null);
    authApi(`${PLATFORM}/tenants/${id}/users/${userId}`, { method: "DELETE" })
      .then(() => loadUsers())
      .catch((e: unknown) => setUserActionError(e instanceof Error ? e.message : "Failed to remove user"));
  };

  const handleDeleteTenant = () => {
    setDeletingTenant(true);
    setDeleteError(null);
    authApi(`${PLATFORM}/tenants/${id}`, { method: "DELETE" })
      .then(() => {
        setDeleteDialogOpen(false);
        onBack();
      })
      .catch((e: unknown) => setDeleteError(e instanceof Error ? e.message : "Failed to delete tenant"))
      .finally(() => setDeletingTenant(false));
  };

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!tenant) return null;

  return (
    <Box>
      <Button onClick={onBack} sx={{ mb: 2 }}>← Back</Button>

      {/* Info card */}
      <Box sx={{ bgcolor: "background.paper", borderRadius: 2, p: 3, mb: 3, boxShadow: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Typography variant="h5" fontWeight={600}>{tenant.name}</Typography>
          <Chip label={tenant.status} color={tenant.status === "active" ? "success" : "default"} />
          <Chip
            label={integrationModeLabel(tenant.integration_mode)}
            color={integrationModeColor(tenant.integration_mode)}
          />
          <Button size="small" variant="outlined" onClick={openModeEdit} sx={{ ml: 0 }}>
            Change Mode
          </Button>
          <Box sx={{ ml: "auto" }}>
            <Button
              variant="outlined"
              color={tenant.status === "active" ? "warning" : "success"}
              size="small"
              disabled={statusUpdating}
              onClick={handleStatusToggle}
            >
              {statusUpdating
                ? <CircularProgress size={16} />
                : tenant.status === "active" ? "Suspend" : "Activate"}
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              sx={{ ml: 1 }}
              disabled={deletingTenant}
              onClick={() => setDeleteDialogOpen(true)}
            >
              Delete
            </Button>
          </Box>
        </Box>

        {statusError && <Alert severity="error" sx={{ mb: 1 }}>{statusError}</Alert>}
        {deleteError && <Alert severity="error" sx={{ mb: 1 }}>{deleteError}</Alert>}

        <Typography variant="body2" color="text.secondary">Slug: {tenant.slug}</Typography>
        <Typography variant="body2" color="text.secondary">
          Created: {new Date(tenant.created_at).toLocaleString()}
        </Typography>

        {/* OCPP token — blurred by default */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
          <Typography variant="body2" color="text.secondary">OCPP Token:</Typography>
          <Typography
            variant="body2"
            sx={{
              fontFamily: "monospace",
              filter: tokenRevealed ? "none" : "blur(6px)",
              userSelect: tokenRevealed ? "auto" : "none",
              transition: "filter 0.2s",
            }}
          >
            {tenant.ocpp_token}
          </Typography>
          <Button size="small" onClick={() => setTokenRevealed((r) => !r)}>
            {tokenRevealed ? "Hide" : "Reveal"}
          </Button>
        </Box>

        <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography variant="body2" color="text.secondary">Gateway Rate Limit:</Typography>
          {tenant.ocpp_gateway_rate_limit_override_enabled ? (
            <Chip
              label={`Override ${tenant.ocpp_gateway_rate_limit_override_max_requests ?? "—"} req/window`}
              color="warning"
              size="small"
            />
          ) : (
            <Chip label="Using gateway default" variant="outlined" size="small" />
          )}
          <Button size="small" variant="outlined" onClick={openRateLimitEdit}>
            Edit
          </Button>
        </Box>
      </Box>

      {/* Stripe section */}
      <Box sx={{ bgcolor: "background.paper", borderRadius: 2, p: 3, mb: 3, boxShadow: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>Stripe Billing</Typography>
          <Button size="small" variant="outlined" onClick={openStripeEdit}>
            Edit
          </Button>
        </Box>

        {stripeInfo && !stripeInfo.connected && (
          <Chip label="Not connected to Stripe" color="default" variant="outlined" />
        )}

        {stripeInfo && stripeInfo.connected && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 180 }}>Customer ID:</Typography>
              <Typography variant="body2" sx={{ fontFamily: "monospace" }}>{stripeInfo.stripe_customer_id}</Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 180 }}>Subscription ID:</Typography>
              <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                {stripeInfo.stripe_subscription_id ?? "—"}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 180 }}>Subscription Status:</Typography>
              {stripeInfo.subscription_status ? (
                <Chip
                  label={stripeInfo.subscription_status}
                  color={stripeStatusColor(stripeInfo.subscription_status)}
                  size="small"
                />
              ) : (
                <Typography variant="body2" color="text.secondary">—</Typography>
              )}
            </Box>
            {stripeInfo.current_period_end && (
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 180 }}>Current Period End:</Typography>
                <Typography variant="body2">
                  {new Date(stripeInfo.current_period_end).toLocaleDateString()}
                </Typography>
                {stripeInfo.cancel_at_period_end && (
                  <Chip label="Cancels at period end" color="warning" size="small" />
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Finance section */}
      <Box sx={{ bgcolor: "background.paper", borderRadius: 2, p: 3, mb: 3, boxShadow: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>Finance Operations</Typography>
          <Button size="small" variant="outlined" onClick={openFinanceEdit}>
            Edit tax profile
          </Button>
          <Button size="small" variant="outlined" onClick={runFinanceReconciliation} disabled={financeBatchLoading}>
            Run reconciliation
          </Button>
          <Button size="small" variant="outlined" onClick={runFinanceDunning} disabled={financeBatchLoading}>
            Run dunning
          </Button>
        </Box>
        {financeActionError && <Alert severity="error" sx={{ mb: 2 }}>{financeActionError}</Alert>}
        {financeSummary && (
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
            <Chip label={`Open documents: ${financeSummary.open_documents}`} />
            <Chip label={`Overdue: ${financeSummary.overdue_documents}`} color={financeSummary.overdue_documents ? "warning" : "default"} />
            <Chip label={`Open batches: ${financeSummary.open_reconciliation_batches}`} />
            <Chip label={`Open dunning: ${financeSummary.open_dunning_cases}`} />
            <Chip label={`Outstanding: ${financeSummary.outstanding_amount.toFixed(2)}`} color="primary" />
          </Box>
        )}
        {financeTaxProfile ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Tax profile: {financeTaxProfile.default_country_code ?? "—"} · VAT {financeTaxProfile.default_vat_rate_percent.toFixed(2)}% ·
              {financeTaxProfile.reverse_charge_enabled ? " reverse charge" : " standard"}
            </Typography>
            {financeTaxProfile.vat_id && (
              <Typography variant="body2" color="text.secondary">VAT ID: {financeTaxProfile.vat_id}</Typography>
            )}
            {financeTaxProfile.notes && (
              <Typography variant="body2" color="text.secondary">Notes: {financeTaxProfile.notes}</Typography>
            )}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No tenant tax profile configured.
          </Typography>
        )}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Document</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Gross</TableCell>
              <TableCell>Due</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {financeDocuments.map((doc) => (
              <TableRow key={doc.id}>
                <TableCell>{doc.invoice_number}</TableCell>
                <TableCell>{doc.document_type}</TableCell>
                <TableCell>
                  <Chip label={doc.status} size="small" />
                </TableCell>
                <TableCell align="right">{doc.total_gross.toFixed(2)} {doc.currency}</TableCell>
                <TableCell>{doc.due_date ? new Date(doc.due_date).toLocaleDateString() : "—"}</TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Button size="small" variant="outlined" disabled={financeActionLoadingId === doc.id} onClick={() => createFinanceCreditNote(doc.id)}>
                      Credit note
                    </Button>
                    <Button size="small" variant="outlined" disabled={financeActionLoadingId === doc.id} onClick={() => createFinanceRefund(doc.id)}>
                      Refund
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {financeDocuments.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary">No finance documents yet.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Payout tracking</Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
            <TextField size="small" label="Provider" value={financePayoutProvider} onChange={(e) => setFinancePayoutProvider(e.target.value)} />
            <TextField size="small" label="Payout ref" value={financePayoutExternalId} onChange={(e) => setFinancePayoutExternalId(e.target.value)} />
            <TextField size="small" label="Gross" type="number" value={financePayoutGross} onChange={(e) => setFinancePayoutGross(e.target.value)} />
            <TextField size="small" label="Fee" type="number" value={financePayoutFee} onChange={(e) => setFinancePayoutFee(e.target.value)} />
            <Select size="small" value={financePayoutStatus} onChange={(e) => setFinancePayoutStatus(e.target.value)}>
              <MenuItem value="pending">pending</MenuItem>
              <MenuItem value="paid">paid</MenuItem>
              <MenuItem value="failed">failed</MenuItem>
            </Select>
            <Button size="small" variant="contained" onClick={createFinancePayout} disabled={financeBatchLoading}>Add payout</Button>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Ref</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Net</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {financePayouts.slice(0, 5).map((payout) => (
                <TableRow key={payout.id}>
                  <TableCell>{payout.provider_payout_id}</TableCell>
                  <TableCell>{payout.status}</TableCell>
                  <TableCell align="right">{payout.amount_net.toFixed(2)} {payout.currency}</TableCell>
                  <TableCell>{new Date(payout.created_at).toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Reconciliation batches</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Reference</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Variance</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {financeReconciliationBatches.slice(0, 5).map((batch) => (
                <TableRow key={batch.id}>
                  <TableCell>{batch.statement_reference ?? `batch-${batch.id}`}</TableCell>
                  <TableCell>{batch.status}</TableCell>
                  <TableCell align="right">{batch.variance_amount.toFixed(2)} {batch.currency}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Dunning cases</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Invoice</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Step</TableCell>
                <TableCell align="right">Overdue</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {financeDunningCases.slice(0, 5).map((dunningCase) => (
                <TableRow key={dunningCase.id}>
                  <TableCell>{dunningCase.invoice_id}</TableCell>
                  <TableCell>{dunningCase.status}</TableCell>
                  <TableCell>{dunningCase.dunning_step}</TableCell>
                  <TableCell align="right">{dunningCase.overdue_amount.toFixed(2)} {dunningCase.currency}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Recent finance audit events</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Entity</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {financeAuditEvents.slice(0, 8).map((event) => (
                <TableRow key={event.id}>
                  <TableCell>{new Date(event.created_at).toLocaleString()}</TableCell>
                  <TableCell>{event.entity_type}</TableCell>
                  <TableCell>{event.action}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Box>

      {/* Portal Admin Users */}
      <Box sx={{ bgcolor: "background.paper", borderRadius: 2, p: 3, mb: 3, boxShadow: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>Portal Users</Typography>
          <Button size="small" variant="contained" onClick={() => { setAddUserError(null); setAddUserOpen(true); }}>
            Add User
          </Button>
        </Box>
        {userActionError && <Alert severity="error" sx={{ mb: 1 }}>{userActionError}</Alert>}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((u) => {
              const permissionGroup = u.permission_group ?? u.role ?? "operator";
              return (
              <TableRow key={u.id}>
                <TableCell>{u.email ?? <Typography variant="body2" color="text.secondary">{u.auth0_sub}</Typography>}</TableCell>
                <TableCell>{u.name ?? "—"}</TableCell>
                <TableCell>
                  <Chip
                    label={permissionGroup}
                    color={permissionGroup === "admin" ? "primary" : "default"}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip label={u.status} color={u.status === "active" ? "success" : "warning"} size="small" />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button size="small" variant="outlined" onClick={() => handleChangeRole(u.id, permissionGroup)}>
                      Make {permissionGroup === "admin" ? "Operator" : "Admin"}
                    </Button>
                    <Button size="small" color="error" onClick={() => handleRemoveUser(u.id)}>
                      Remove
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
              );
            })}
            {users.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary">No portal users.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Driver Memberships */}
      <Typography variant="h6" sx={{ mb: 1 }}>Driver Memberships</Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Driver ID</TableCell>
            <TableCell>Join Mode</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Created</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {memberships.map((m) => (
            <TableRow key={m.id}>
              <TableCell>{m.id}</TableCell>
              <TableCell>
                <Button size="small" onClick={() => onOpenDriver(m.driver_id)}>
                  {m.driver_id}
                </Button>
              </TableCell>
              <TableCell>{m.join_mode}</TableCell>
              <TableCell>
                <Chip label={m.status} color={m.status === "active" ? "success" : "warning"} size="small" />
              </TableCell>
              <TableCell>{new Date(m.created_at).toLocaleDateString()}</TableCell>
            </TableRow>
          ))}
          {memberships.length === 0 && (
            <TableRow>
              <TableCell colSpan={5}>
                <Typography variant="body2" color="text.secondary">No memberships.</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={deleteDialogOpen} onClose={() => !deletingTenant && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete tenant?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This permanently deletes {tenant.name} and related tenant data. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deletingTenant}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteTenant} disabled={deletingTenant}>
            {deletingTenant ? <CircularProgress size={18} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Stripe edit dialog */}
      <Dialog open={stripeEditOpen} onClose={() => !stripeSaving && setStripeEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Stripe Connection</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          {stripeEditError && <Alert severity="error">{stripeEditError}</Alert>}
          <TextField
            label="Stripe Customer ID"
            value={stripeCustomerIdInput}
            onChange={(e) => setStripeCustomerIdInput(e.target.value)}
            disabled={stripeSaving}
            helperText="e.g. cus_ABC123 — leave blank to disconnect"
            fullWidth
          />
          <TextField
            label="Stripe Subscription ID"
            value={stripeSubIdInput}
            onChange={(e) => setStripeSubIdInput(e.target.value)}
            disabled={stripeSaving}
            helperText="e.g. sub_XYZ456 — leave blank if not yet subscribed"
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStripeEditOpen(false)} disabled={stripeSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleStripeSave} disabled={stripeSaving}>
            {stripeSaving ? <CircularProgress size={18} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={financeEditOpen} onClose={() => !financeSaving && setFinanceEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Tax Profile</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          {financeActionError && <Alert severity="error">{financeActionError}</Alert>}
          <TextField
            label="Default country code"
            value={financeCountryCode}
            onChange={(e) => setFinanceCountryCode(e.target.value.toUpperCase())}
            disabled={financeSaving}
            helperText="Used when no location tax jurisdiction is set"
            fullWidth
          />
          <TextField
            label="VAT ID"
            value={financeVatId}
            onChange={(e) => setFinanceVatId(e.target.value)}
            disabled={financeSaving}
            fullWidth
          />
          <TextField
            label="Default VAT rate"
            type="number"
            value={financeVatRate}
            onChange={(e) => setFinanceVatRate(e.target.value)}
            disabled={financeSaving}
            inputProps={{ min: 0, step: "any" }}
            fullWidth
          />
          <Select
            value={financeReverseCharge ? "enabled" : "disabled"}
            onChange={(e) => setFinanceReverseCharge(e.target.value === "enabled")}
            disabled={financeSaving}
            fullWidth
          >
            <MenuItem value="disabled">Standard VAT</MenuItem>
            <MenuItem value="enabled">Reverse charge</MenuItem>
          </Select>
          <TextField
            label="Notes"
            value={financeNotes}
            onChange={(e) => setFinanceNotes(e.target.value)}
            disabled={financeSaving}
            multiline
            minRows={2}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFinanceEditOpen(false)} disabled={financeSaving}>Cancel</Button>
          <Button variant="contained" onClick={saveFinanceTaxProfile} disabled={financeSaving}>
            {financeSaving ? <CircularProgress size={18} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add user dialog */}
      <Dialog open={addUserOpen} onClose={() => !addingUser && setAddUserOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Portal User</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          {addUserError && <Alert severity="error">{addUserError}</Alert>}
          <TextField
            label="Email"
            value={addUserEmail}
            onChange={(e) => setAddUserEmail(e.target.value)}
            disabled={addingUser}
            helperText="If the account already exists, claims are applied immediately. Otherwise access is activated after first login."
            fullWidth
          />
          <TextField
            label="Name (optional)"
            value={addUserName}
            onChange={(e) => setAddUserName(e.target.value)}
            disabled={addingUser}
            fullWidth
          />
          <Select
            value={addUserRole}
            onChange={(e) => setAddUserRole(e.target.value)}
            disabled={addingUser}
            fullWidth
          >
            <MenuItem value="admin">Admin</MenuItem>
            <MenuItem value="operator">Operator</MenuItem>
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddUserOpen(false)} disabled={addingUser}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddUser}
            disabled={addingUser || !addUserEmail.trim()}
          >
            {addingUser ? <CircularProgress size={18} /> : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Integration mode dialog */}
      <Dialog open={modeEditOpen} onClose={() => !modeSaving && setModeEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Driver Visibility Mode</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          {modeEditError && <Alert severity="error">{modeEditError}</Alert>}
          <Select
            value={modeInput}
            onChange={(e) => setModeInput(e.target.value)}
            disabled={modeSaving}
            fullWidth
          >
            <MenuItem value="invite_only">Invite Only — CPO controls access via invite rules</MenuItem>
            <MenuItem value="shared">Shared — All drivers are visible in anonymized form</MenuItem>
            <MenuItem value="isolated">Isolated — No eMSP integration</MenuItem>
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModeEditOpen(false)} disabled={modeSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleModeSave} disabled={modeSaving}>
            {modeSaving ? <CircularProgress size={18} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rateLimitEditOpen} onClose={() => !rateLimitSaving && setRateLimitEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Gateway Rate Limit Override</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          {rateLimitEditError && <Alert severity="error">{rateLimitEditError}</Alert>}
          <Select
            value={rateLimitOverrideEnabledInput}
            onChange={(e) => setRateLimitOverrideEnabledInput(e.target.value)}
            disabled={rateLimitSaving}
            fullWidth
          >
            <MenuItem value="disabled">Disabled — use the gateway default</MenuItem>
            <MenuItem value="enabled">Enabled — apply a tenant-specific limit</MenuItem>
          </Select>
          <TextField
            label="Max requests per window"
            type="number"
            value={rateLimitMaxRequestsInput}
            onChange={(e) => setRateLimitMaxRequestsInput(e.target.value)}
            disabled={rateLimitSaving}
            helperText="Required when the override is enabled."
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRateLimitEditOpen(false)} disabled={rateLimitSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleRateLimitSave} disabled={rateLimitSaving}>
            {rateLimitSaving ? <CircularProgress size={18} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
